var _a;
import _ from 'lodash';
/**
 * Formatter types that belong to a page.
 */
export var PAGE_FORMATTER_TYPES = new Set([
    "list" /* FormatterType.list */,
    "detail" /* FormatterType.detail */,
    "edit" /* FormatterType.edit */,
    "create" /* FormatterType.create */,
    "page" /* FormatterType.page */,
]);
/**
 * Formatter types that belong to a page.
 */
export var MAIN_FORMATTER_COMPONENT_TYPES = new Set([
    "CardEditor" /* FormatterComponentType.CardEditor */,
    "EnumFormatter" /* FormatterComponentType.EnumFormatter */,
    "FormatStringFormatter" /* FormatterComponentType.FormatStringFormatter */,
    "PluginFormatter" /* FormatterComponentType.PluginFormatter */,
]);
/**
 * Pretty names for all formatter types.
 */
export var FORMATTER_TYPE_NAMES = (_a = {},
    _a["list" /* FormatterType.list */] = 'List',
    _a["create" /* FormatterType.create */] = 'Create',
    _a["detail" /* FormatterType.detail */] = 'Detail',
    _a["edit" /* FormatterType.edit */] = 'Edit',
    _a["page" /* FormatterType.page */] = 'Authenticated page',
    _a["popup" /* FormatterType.popup */] = 'Popup',
    _a["sub" /* FormatterType.sub */] = 'Sub',
    _a["displayName" /* FormatterType.displayName */] = 'Display name',
    _a["excel" /* FormatterType.excel */] = 'Excel',
    _a["publicPage" /* FormatterType.publicPage */] = 'Public page',
    _a);
/**
 * Information for formatter types.
 *
 * This does not contain all types, because they have no dedicated information about them.
 */
export var FORMATTER_TYPE_INFO = [
    {
        type: "page" /* FormatterType.page */,
        icon: 'lock',
        color: 'warning',
    },
    {
        type: "publicPage" /* FormatterType.publicPage */,
        icon: 'public',
        color: 'success',
    },
];
export var FORMATTER_TYPE_INFO_BY_TYPE = _.keyBy(FORMATTER_TYPE_INFO, 'type');
/**
 * Maps formatter types to its own order.
 *
 * This can be used to show formatters in a logical order.
 */
export var FORMATTER_TYPE_ORDER = _([
    "list" /* FormatterType.list */,
    "create" /* FormatterType.create */,
    "detail" /* FormatterType.detail */,
    "edit" /* FormatterType.edit */,
    "page" /* FormatterType.page */,
    "popup" /* FormatterType.popup */,
    "sub" /* FormatterType.sub */,
    "displayName" /* FormatterType.displayName */,
    "excel" /* FormatterType.excel */,
])
    .map(function (type, index) { return ({ type: type, index: index }); })
    .keyBy('type')
    .mapValues('index')
    .value();
export var FORMATTER_ORDERING = [function (page) { return FORMATTER_TYPE_ORDER[page.formatterType]; }, 'name'];
/**
 * Contains information about formatter components.
 *
 * This is intended to be a single place where we keep track of the different kinds of information about components,
 *  so if there is new information about components, feel free to add it here.
 */
export var FORMATTER_COMPONENT_INFO = [
    // Pages
    {
        type: "CardEditor" /* FormatterComponentType.CardEditor */,
        displayName: 'Card editor',
        formatterTypes: ["detail" /* FormatterType.detail */, "create" /* FormatterType.create */, "edit" /* FormatterType.edit */, "sub" /* FormatterType.sub */],
        consumers: ["detail" /* FormatterType.detail */, "create" /* FormatterType.create */, "edit" /* FormatterType.edit */, "list" /* FormatterType.list */],
        isFirstClass: true,
        isConfigurable: true,
        dataTypes: [
            "EntityDataType" /* DataTypeDiscriminator.EntityDataType */,
            "ForeignKeyDataType" /* DataTypeDiscriminator.ForeignKeyDataType */,
            "ProfileEntityType" /* DataTypeDiscriminator.ProfileEntityType */,
        ],
        primitiveTypes: [],
    },
    {
        type: "EntityTable" /* FormatterComponentType.EntityTable */,
        displayName: 'Entity table',
        // The EntityTable can be a sub-formatter when it is used by a FKTableEditor in its configuration.
        formatterTypes: ["list" /* FormatterType.list */, "sub" /* FormatterType.sub */],
        consumers: [],
        isFirstClass: true,
        isConfigurable: true,
        dataTypes: ["EntityDataType" /* DataTypeDiscriminator.EntityDataType */, "ProfileEntityType" /* DataTypeDiscriminator.ProfileEntityType */],
        primitiveTypes: [],
    },
    // Formatters
    {
        type: "ArrayEditor" /* FormatterComponentType.ArrayEditor */,
        displayName: 'Array editor',
        formatterTypes: ["sub" /* FormatterType.sub */],
        consumers: ["create" /* FormatterType.create */, "edit" /* FormatterType.edit */],
        isFirstClass: false,
        isConfigurable: true,
        dataTypes: ["ArrayDataType" /* DataTypeDiscriminator.ArrayDataType */],
        primitiveTypes: [],
    },
    {
        type: "ArrayFormatter" /* FormatterComponentType.ArrayFormatter */,
        displayName: 'Array formatter',
        formatterTypes: ["sub" /* FormatterType.sub */],
        consumers: ["detail" /* FormatterType.detail */, "create" /* FormatterType.create */, "edit" /* FormatterType.edit */, "list" /* FormatterType.list */],
        isFirstClass: false,
        isConfigurable: true,
        dataTypes: ["ArrayDataType" /* DataTypeDiscriminator.ArrayDataType */],
        primitiveTypes: [],
    },
    {
        type: "BoolCheckboxFormatter" /* FormatterComponentType.BoolCheckboxFormatter */,
        displayName: 'Boolean checkbox',
        formatterTypes: ["sub" /* FormatterType.sub */],
        consumers: ["create" /* FormatterType.create */, "edit" /* FormatterType.edit */],
        isFirstClass: false,
        isConfigurable: false,
        dataTypes: ["PrimitiveDataType" /* DataTypeDiscriminator.PrimitiveDataType */],
        primitiveTypes: ["Boolean" /* PrimitiveTypeDiscriminator.Boolean */],
    },
    {
        type: "BoolRadioEditor" /* FormatterComponentType.BoolRadioEditor */,
        displayName: 'Boolean radio',
        formatterTypes: ["sub" /* FormatterType.sub */],
        consumers: ["create" /* FormatterType.create */, "edit" /* FormatterType.edit */],
        isFirstClass: false,
        isConfigurable: false,
        dataTypes: ["PrimitiveDataType" /* DataTypeDiscriminator.PrimitiveDataType */],
        primitiveTypes: ["Boolean" /* PrimitiveTypeDiscriminator.Boolean */],
    },
    {
        type: "CoordinateFormatter" /* FormatterComponentType.CoordinateFormatter */,
        displayName: 'Coordinate formatter',
        formatterTypes: ["sub" /* FormatterType.sub */],
        consumers: ["detail" /* FormatterType.detail */],
        isFirstClass: false,
        isConfigurable: false,
        dataTypes: ["PrimitiveDataType" /* DataTypeDiscriminator.PrimitiveDataType */],
        primitiveTypes: ["String" /* PrimitiveTypeDiscriminator.String */],
    },
    {
        type: "DateTimeEditor" /* FormatterComponentType.DateTimeEditor */,
        displayName: 'DateTime editor',
        formatterTypes: ["sub" /* FormatterType.sub */],
        consumers: ["create" /* FormatterType.create */, "edit" /* FormatterType.edit */],
        isFirstClass: false,
        isConfigurable: false,
        dataTypes: ["PrimitiveDataType" /* DataTypeDiscriminator.PrimitiveDataType */],
        primitiveTypes: ["DateTime" /* PrimitiveTypeDiscriminator.DateTime */],
    },
    {
        type: "EnumFormatter" /* FormatterComponentType.EnumFormatter */,
        displayName: 'Enum formatter',
        formatterTypes: ["sub" /* FormatterType.sub */],
        consumers: ["detail" /* FormatterType.detail */, "create" /* FormatterType.create */, "edit" /* FormatterType.edit */, "list" /* FormatterType.list */],
        isFirstClass: true,
        isConfigurable: true,
        dataTypes: ["EnumDataType" /* DataTypeDiscriminator.EnumDataType */],
        primitiveTypes: [],
    },
    {
        type: "EnumRadioEditor" /* FormatterComponentType.EnumRadioEditor */,
        displayName: 'Enum radio editor',
        formatterTypes: ["sub" /* FormatterType.sub */],
        consumers: ["create" /* FormatterType.create */, "edit" /* FormatterType.edit */],
        isFirstClass: false,
        isConfigurable: true,
        dataTypes: ["EnumDataType" /* DataTypeDiscriminator.EnumDataType */],
        primitiveTypes: [],
    },
    {
        type: "EnumDropdownEditor" /* FormatterComponentType.EnumDropdownEditor */,
        displayName: 'Enum dropdown editor',
        formatterTypes: ["sub" /* FormatterType.sub */],
        consumers: ["create" /* FormatterType.create */, "edit" /* FormatterType.edit */],
        isFirstClass: false,
        isConfigurable: true,
        dataTypes: ["EnumDataType" /* DataTypeDiscriminator.EnumDataType */],
        primitiveTypes: [],
    },
    {
        type: "FKRadioEditor" /* FormatterComponentType.FKRadioEditor */,
        displayName: 'FK radio editor',
        formatterTypes: ["sub" /* FormatterType.sub */],
        consumers: ["create" /* FormatterType.create */, "edit" /* FormatterType.edit */],
        isFirstClass: false,
        isConfigurable: true,
        dataTypes: ["ForeignKeyDataType" /* DataTypeDiscriminator.ForeignKeyDataType */],
        primitiveTypes: [],
    },
    {
        type: "SearchableFKDropdownEditor" /* FormatterComponentType.SearchableFKDropdownEditor */,
        displayName: 'Searchable FK dropdown editor',
        formatterTypes: ["sub" /* FormatterType.sub */],
        consumers: ["create" /* FormatterType.create */, "edit" /* FormatterType.edit */],
        isFirstClass: false,
        isConfigurable: true,
        dataTypes: ["ForeignKeyDataType" /* DataTypeDiscriminator.ForeignKeyDataType */],
        primitiveTypes: [],
    },
    {
        type: "FKTableEditor" /* FormatterComponentType.FKTableEditor */,
        displayName: 'FK table editor',
        formatterTypes: ["sub" /* FormatterType.sub */],
        consumers: ["create" /* FormatterType.create */, "edit" /* FormatterType.edit */],
        isFirstClass: false,
        isConfigurable: true,
        dataTypes: ["ArrayDataType" /* DataTypeDiscriminator.ArrayDataType */, "ForeignKeyDataType" /* DataTypeDiscriminator.ForeignKeyDataType */],
        primitiveTypes: [],
    },
    {
        type: "FormatStringFormatter" /* FormatterComponentType.FormatStringFormatter */,
        displayName: 'Format string',
        formatterTypes: ["sub" /* FormatterType.sub */, "displayName" /* FormatterType.displayName */],
        consumers: ["detail" /* FormatterType.detail */, "create" /* FormatterType.create */, "edit" /* FormatterType.edit */, "list" /* FormatterType.list */],
        isFirstClass: true,
        isConfigurable: true,
        dataTypes: [
            "ArrayDataType" /* DataTypeDiscriminator.ArrayDataType */,
            "EntityDataType" /* DataTypeDiscriminator.EntityDataType */,
            "EnumDataType" /* DataTypeDiscriminator.EnumDataType */,
            "ForeignKeyDataType" /* DataTypeDiscriminator.ForeignKeyDataType */,
            "PrimitiveDataType" /* DataTypeDiscriminator.PrimitiveDataType */,
            "ProfileEntityType" /* DataTypeDiscriminator.ProfileEntityType */,
        ],
        primitiveTypes: [
            "String" /* PrimitiveTypeDiscriminator.String */,
            "Int" /* PrimitiveTypeDiscriminator.Int */,
            "Boolean" /* PrimitiveTypeDiscriminator.Boolean */,
            "DateTime" /* PrimitiveTypeDiscriminator.DateTime */,
            "Decimal" /* PrimitiveTypeDiscriminator.Decimal */,
        ],
    },
    {
        type: "MultilinePrimitiveFormatter" /* FormatterComponentType.MultilinePrimitiveFormatter */,
        displayName: 'Multiline primitive formatter',
        formatterTypes: ["sub" /* FormatterType.sub */],
        consumers: ["detail" /* FormatterType.detail */, "create" /* FormatterType.create */, "edit" /* FormatterType.edit */],
        isFirstClass: false,
        isConfigurable: false,
        dataTypes: ["PrimitiveDataType" /* DataTypeDiscriminator.PrimitiveDataType */],
        primitiveTypes: ["String" /* PrimitiveTypeDiscriminator.String */],
    },
    {
        type: "PrimitiveFormatter" /* FormatterComponentType.PrimitiveFormatter */,
        displayName: 'Primitive formatter',
        formatterTypes: ["sub" /* FormatterType.sub */],
        consumers: ["detail" /* FormatterType.detail */, "create" /* FormatterType.create */, "edit" /* FormatterType.edit */, "list" /* FormatterType.list */],
        isFirstClass: false,
        isConfigurable: false,
        dataTypes: ["PrimitiveDataType" /* DataTypeDiscriminator.PrimitiveDataType */],
        primitiveTypes: [
            "String" /* PrimitiveTypeDiscriminator.String */,
            "Int" /* PrimitiveTypeDiscriminator.Int */,
            "Boolean" /* PrimitiveTypeDiscriminator.Boolean */,
            "DateTime" /* PrimitiveTypeDiscriminator.DateTime */,
            "Decimal" /* PrimitiveTypeDiscriminator.Decimal */,
        ],
    },
    {
        type: "TextboxEditor" /* FormatterComponentType.TextboxEditor */,
        displayName: 'Textbox editor',
        formatterTypes: ["sub" /* FormatterType.sub */],
        consumers: ["create" /* FormatterType.create */, "edit" /* FormatterType.edit */],
        isFirstClass: false,
        isConfigurable: false,
        dataTypes: ["PrimitiveDataType" /* DataTypeDiscriminator.PrimitiveDataType */],
        primitiveTypes: ["String" /* PrimitiveTypeDiscriminator.String */],
    },
    {
        type: "FileDropzone" /* FormatterComponentType.FileDropzone */,
        displayName: 'File dropzone',
        formatterTypes: ["sub" /* FormatterType.sub */],
        consumers: ["create" /* FormatterType.create */, "edit" /* FormatterType.edit */],
        isFirstClass: false,
        isConfigurable: false,
        dataTypes: ["FileDataType" /* DataTypeDiscriminator.FileDataType */, "ArrayDataType" /* DataTypeDiscriminator.ArrayDataType */],
        primitiveTypes: [],
    },
    {
        type: "FileViewFormatter" /* FormatterComponentType.FileViewFormatter */,
        displayName: 'File view formatter',
        formatterTypes: ["sub" /* FormatterType.sub */],
        consumers: ["detail" /* FormatterType.detail */],
        isFirstClass: false,
        isConfigurable: false,
        dataTypes: ["FileDataType" /* DataTypeDiscriminator.FileDataType */],
        primitiveTypes: [],
    },
    {
        type: "FileDownloadFormatter" /* FormatterComponentType.FileDownloadFormatter */,
        displayName: 'File download formatter',
        formatterTypes: ["sub" /* FormatterType.sub */],
        consumers: ["detail" /* FormatterType.detail */, "create" /* FormatterType.create */, "edit" /* FormatterType.edit */, "list" /* FormatterType.list */],
        isFirstClass: false,
        isConfigurable: false,
        dataTypes: ["FileDataType" /* DataTypeDiscriminator.FileDataType */],
        primitiveTypes: [],
    },
    {
        type: "PluginFormatter" /* FormatterComponentType.PluginFormatter */,
        displayName: 'Plugin',
        formatterTypes: [
            "create" /* FormatterType.create */,
            "edit" /* FormatterType.edit */,
            "detail" /* FormatterType.detail */,
            "sub" /* FormatterType.sub */,
            "page" /* FormatterType.page */,
            "list" /* FormatterType.list */,
            "popup" /* FormatterType.popup */,
        ],
        consumers: ["detail" /* FormatterType.detail */, "create" /* FormatterType.create */, "edit" /* FormatterType.edit */, "list" /* FormatterType.list */],
        isFirstClass: true,
        isConfigurable: true,
        dataTypes: [
            "ArrayDataType" /* DataTypeDiscriminator.ArrayDataType */,
            "EntityDataType" /* DataTypeDiscriminator.EntityDataType */,
            "EnumDataType" /* DataTypeDiscriminator.EnumDataType */,
            "FileDataType" /* DataTypeDiscriminator.FileDataType */,
            "ForeignKeyDataType" /* DataTypeDiscriminator.ForeignKeyDataType */,
            "PrimitiveDataType" /* DataTypeDiscriminator.PrimitiveDataType */,
            "ProfileEntityType" /* DataTypeDiscriminator.ProfileEntityType */,
            null, // the plugin can also be used without a data type
        ],
        primitiveTypes: [
            "String" /* PrimitiveTypeDiscriminator.String */,
            "Int" /* PrimitiveTypeDiscriminator.Int */,
            "Boolean" /* PrimitiveTypeDiscriminator.Boolean */,
            "DateTime" /* PrimitiveTypeDiscriminator.DateTime */,
            "Decimal" /* PrimitiveTypeDiscriminator.Decimal */,
        ],
    },
];
export var FORMATTER_COMPONENTS_BY_DATA_TYPE = _(FORMATTER_COMPONENT_INFO)
    .flatMap(function (component) { return _.map(component.dataTypes, function (dataType) { return ({ component: component, dataType: dataType }); }); })
    .groupBy('dataType')
    .mapValues(function (components) { return _.map(components, 'component'); })
    .value();
export var FORMATTER_COMPONENTS_BY_PRIMITIVE_TYPE = _(FORMATTER_COMPONENT_INFO)
    .flatMap(function (component) { return _.map(component.primitiveTypes, function (primitiveType) { return ({ component: component, primitiveType: primitiveType }); }); })
    .groupBy('primitiveType')
    .mapValues(function (components) { return _.map(components, 'component'); })
    .value();
export var FORMATTER_COMPONENTS_BY_TYPE = _.keyBy(FORMATTER_COMPONENT_INFO, 'type');
export var hasEntryFormatter = function (formatter) {
    return _.hasIn(formatter, 'settings.entryFormatterId');
};
export var hasAttributes = function (formatter) {
    return _.hasIn(formatter, 'settings.attributes');
};
export var hasEntryFormatterOrListFormatter = function (formatter) {
    return _.hasIn(formatter, 'settings.entryFormatterId') && _.hasIn(formatter, 'settings.listFormatterId');
};
/**
 * Given a list of formatters and a target formatter ID, finds all formatters
 * that directly or indirectly depend on the target formatter. This includes
 * "first-class" formatters referencing the target formatter in their settings
 * as well as formatters that have the target formatter set as an entry or list formatter.
 *
 * The search is done by:
 * 1. Building a map of formatter dependencies using their settings.
 * 2. Performing a depth-first search (DFS) starting at the target formatter.
 *    Each time a formatter is found to depend on the currently searched ID,
 *    that formatter is added to a queue for further searching.
 * 3. Collecting formatters marked as "first-class" (e.g., CardEditor, EntityTable)
 *    that reference the target formatter into the result.
 * 4. Returning all found dependent formatters, sorted alphabetically by `name`.
 *
 * @param {Formatter[]} formatters - A list of all known formatters.
 * @param {string} formatterId - The ID of the formatter to find dependents for.
 * @returns {Formatter[]} - A sorted array of formatters that reference the target formatter.
 */
export var findPagesUsingCurrentFormatter = function (formatters, formatterId) {
    var formatterDependencies = _(formatters)
        .keyBy('id')
        .mapValues(function (formatter) {
        var formatterComponent = FORMATTER_COMPONENTS_BY_TYPE[formatter === null || formatter === void 0 ? void 0 : formatter.componentType];
        if (formatterComponent === null || formatterComponent === void 0 ? void 0 : formatterComponent.isFirstClass) {
            switch (formatter.componentType) {
                case "CardEditor" /* FormatterComponentType.CardEditor */:
                    return _.map(formatter.settings.attributes, 'formatterId');
                case "EntityTable" /* FormatterComponentType.EntityTable */:
                    return _.map(formatter.settings.attributes, 'formatterId');
                default:
                    return [];
            }
        }
        else if (hasEntryFormatterOrListFormatter(formatter)) {
            var entryFormatterId = formatter.settings.entryFormatterId;
            var listFormatterId = formatter.settings.listFormatterId;
            return [entryFormatterId, listFormatterId];
        }
        else if (hasEntryFormatter(formatter)) {
            return [formatter.settings.entryFormatterId];
        }
        else
            return [];
    })
        .mapValues(function (formatterIds) { return new Set(formatterIds); })
        .value();
    var pending = [formatterId];
    var searched = new Set(pending);
    var formattersById = _.keyBy(formatters, 'id');
    var result = [];
    var _loop_1 = function () {
        var id = pending.pop();
        var formatter = formattersById[id];
        // This causes crashes during development when the page reloads.
        if (!formatter)
            return "continue";
        // If it's first-class and not the original formatter, add it to the result.
        if (FORMATTER_COMPONENTS_BY_TYPE[formatter.componentType].isFirstClass && formatter.id !== formatterId) {
            result.push(formatter);
        }
        // pickBy() is like a filter for Dictionaries
        var filtered = _.pickBy(formatterDependencies, function (ids) { return ids.has(id); });
        var parents = _.keys(filtered);
        for (var _i = 0, parents_1 = parents; _i < parents_1.length; _i++) {
            var parentId = parents_1[_i];
            if (!searched.has(parentId)) {
                pending.push(parentId);
                searched.add(parentId);
            }
        }
    };
    while (pending.length) {
        _loop_1();
    }
    return _.sortBy(result, 'name');
};
